/**
 * Textarea: multi-line text, for blurbs, notes and messages to editors (cockatrice-ui `src/ui/Textarea.jsx`, ported to
 * hyperscript; PLAN.md §0).
 * `autoGrow` grows with the text (up to maxRows). `showCount` with maxLength shows "120 / 500".
 * `font: 'serif'` for book text such as a blurb or dedication.
 */
import './css/textarea.css';
import { createEffect, createSignal, createUniqueId, mergeProps, onMount, splitProps } from 'solid-js';
import { useFieldAttrs, wrapControl } from './Field.ts';
import { component, el, spread, when } from './solid.ts';
import type { Attributes, Child, On } from './solid.ts';
import { uiText } from './text.ts';

export interface TextareaProps extends Attributes {
  /** Visible label. When given (or help/error), the control wraps itself in a Field. */
  readonly label?: Child;
  /** Help text under the control, linked with aria-describedby. */
  readonly help?: Child;
  /** Error message. Sets aria-invalid and shows the message with a warning icon. */
  readonly error?: Child;
  /** Marks the field required and shows "Required". */
  readonly required?: boolean | undefined;
  /** Shows "Optional" next to the label. */
  readonly optional?: boolean | undefined;
  /** Class for the wrapping Field. */
  readonly fieldClass?: string | undefined;
  /** Controlled value. */
  readonly value?: string | undefined;
  /** Uncontrolled starting value. */
  readonly defaultValue?: string | undefined;
  /** Called on every keystroke. */
  readonly onChange?: ((value: string, event: InputEvent) => void) | undefined;
  /** The textarea's own input handler, run first. */
  readonly onInput?: ((event: InputEvent) => void) | undefined;
  /** Starting height in rows. Default 4. */
  readonly rows?: number | undefined;
  /** Grows with the text up to maxRows. */
  readonly autoGrow?: boolean | undefined;
  /** Most rows when growing. Default 12. */
  readonly maxRows?: number | undefined;
  /** Character limit. */
  readonly maxLength?: number | undefined;
  /** Shows "120 / 400" (needs maxLength). */
  readonly showCount?: boolean | undefined;
  /** serif for book text (blurb, dedication). */
  readonly font?: 'sans' | 'serif' | undefined;
  /** Error styling without a message. */
  readonly invalid?: boolean | undefined;
  /** Disables the textarea. */
  readonly disabled?: boolean | undefined;
  /** Class on the wrapper. */
  readonly class?: string | undefined;
  /** Ref to the <textarea>. */
  readonly ref?: ((el: HTMLTextAreaElement) => void) | undefined;
}

const FIELD_ONLY = ['label', 'help', 'optional', 'fieldClass'] as const;

export default function Textarea(props: TextareaProps): Child {
  const [, rest] = splitProps(props, FIELD_ONLY);
  return wrapControl(props, () => component(TextareaInput, rest));
}

function TextareaInput(props: TextareaProps): Child {
  const p = mergeProps({ rows: 4, maxRows: 12, font: 'sans' } as const, props);
  const [local, rest] = splitProps(p, [
    'id',
    'rows',
    'maxRows',
    'autoGrow',
    'showCount',
    'maxLength',
    'font',
    'value',
    'defaultValue',
    'onChange',
    'onInput',
    'error',
    'invalid',
    'required',
    'disabled',
    'class',
    'ref',
    'aria-describedby',
  ]);
  const f = useFieldAttrs(local);
  const countId = createUniqueId();
  const [len, setLen] = createSignal((local.value ?? local.defaultValue ?? '').length);
  let box: HTMLTextAreaElement | undefined;

  const grow = (): void => {
    if (local.autoGrow !== true || !box) return;
    const cs = getComputedStyle(box);
    const px = (value: string): number => parseFloat(value) || 0;
    const borders = px(cs.borderTopWidth) + px(cs.borderBottomWidth);
    const max = (px(cs.lineHeight) || 21) * local.maxRows + px(cs.paddingTop) + px(cs.paddingBottom) + borders;
    box.style.height = 'auto';
    const want = box.scrollHeight + borders;
    box.style.height = `${Math.min(want, max)}px`;
    box.style.overflowY = want > max ? 'auto' : 'hidden';
  };

  onMount(grow);
  createEffect(() => {
    if (local.value !== undefined) {
      setLen(local.value.length);
      queueMicrotask(grow);
    }
  });

  const onInput = (event: On<InputEvent, HTMLTextAreaElement>): void => {
    local.onInput?.(event);
    setLen(event.currentTarget.value.length);
    grow();
    local.onChange?.(event.currentTarget.value, event);
  };

  const counted = (): boolean => local.showCount === true && Boolean(local.maxLength);
  const near = (): boolean => local.maxLength !== undefined && local.maxLength !== 0 && len() >= local.maxLength * 0.9;

  return el(
    'div',
    { class: () => ['ui-textarea', local.class].filter(Boolean).join(' ') },
    el(
      'textarea',
      spread(rest, {
        ref: (node: HTMLTextAreaElement) => {
          box = node;
          local.ref?.(node);
        },
        id: () => f.id(),
        rows: () => local.rows,
        maxLength: () => local.maxLength,
        class: () =>
          ['ui-textarea__input', `ui-textarea__input--${local.font}`, f.invalid() && 'is-invalid', local.autoGrow === true && 'is-autogrow'].filter(Boolean).join(' '),
        value: () => local.value ?? local.defaultValue ?? '',
        onInput,
        required: () => f.required(),
        disabled: () => f.disabled(),
        'aria-invalid': () => (f.invalid() ? 'true' : undefined),
        'aria-describedby': () => [f.describedBy(), counted() && countId].filter(Boolean).join(' ') || undefined,
      }),
    ),
    when(counted, () =>
      el(
        'div',
        { class: () => (near() ? 'ui-textarea__count is-near' : 'ui-textarea__count'), id: countId },
        () => len(),
        ' / ',
        () => local.maxLength,
        el('span', { class: 'sr-only' }, () => ` ${uiText()('ui.textarea.characters')}`),
      ),
    ),
  );
}
